import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';
import { z } from 'zod';

// This is very similar to the device and datasource saved states logic

const ReportGridStateSchema = z.object({
  columnVisibility: z.record(z.boolean()).optional(),
});

type ReportGridState = z.infer<typeof ReportGridStateSchema>;

const getStorageKey = (key: string) => `playoutReports:${key}`;

const getStoredState = (key: string): ReportGridState | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(key));
    if (!stored) return null;
    const result = ReportGridStateSchema.safeParse(JSON.parse(stored));
    if (!result.success) {
      console.error('Invalid stored playout report grid state:', result.error);
      return null;
    }
    return result.data;
  } catch (error) {
    console.error('Failed to get stored playout report grid state:', error);
    return null;
  }
};

const setStoredState = (key: string, state: Partial<ReportGridState>) => {
  try {
    const existing = getStoredState(key) ?? {};
    localStorage.setItem(getStorageKey(key), JSON.stringify({ ...existing, ...state }));
  } catch (error) {
    console.error('Failed to store playout report grid state:', error);
  }
};

const clearStoredState = (key: string) => {
  try {
    localStorage.removeItem(getStorageKey(key));
  } catch (error) {
    console.error('Failed to clear playout report grid state:', error);
  }
};

// Persists per-table column show/hide choices to localStorage, keyed by table.
// Each playout report grid passes a distinct storageKey so toggling columns in
// one table doesn't affect the others. Mirrors useDeviceGridState/
// useDataSourceGridState, including the namespaced state object — any grid
// state added later merges alongside columnVisibility instead of clobbering it.
export const useReportColumnVisibility = (storageKey: string) => {
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>(
    () => getStoredState(storageKey)?.columnVisibility ?? {},
  );
  const [hasPersistedPreferences, setHasPersistedPreferences] = useState(
    () => Object.keys(columnVisibility).length > 0,
  );

  const onColumnVisibilityChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      setColumnVisibility(model);
      setHasPersistedPreferences(true);
      setStoredState(storageKey, { columnVisibility: model });
    },
    [storageKey],
  );

  const resetPreferences = useCallback(() => {
    setColumnVisibility({});
    setHasPersistedPreferences(false);
    clearStoredState(storageKey);
  }, [storageKey]);

  return { columnVisibility, onColumnVisibilityChange, resetPreferences, hasPersistedPreferences };
};
